import qs from 'query-string';
import React, { useCallback, useEffect, useState } from 'react';

import { Confirmation } from '../../../components/ui/Confirmation';
import { Loader } from '../../../components/ui/Loader';
import { Warning } from '../../../components/ui/icons/Warning';
import { useContent } from '../../../hooks/useContent';
import { captureException, captureMessage } from '../../../monitoring/logrocket';
import { fetchAccessToken } from '../../../utils/auth/iframe';
import { clearPersistedParameters } from '../../../utils/persistParameterContext';
import { getSanitizedRedirectUri } from '../../../utils/url';

export const MobileSessionTransitionPage = () => {
  const { accessToken, redirectUrl = 'https://mgmresorts.com' } = qs.parse(location.search || '');
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const { getContentByPath } = useContent();

  const initializeAuthProcess = useCallback(
    async (mobileToken: string) => {
      if (!mobileToken) {
        return;
      }

      try {
        localStorage.setItem('mobile-auth-transfer', '1');
        const token = await fetchAccessToken(mobileToken);

        if (!token) {
          throw Error('Failed to transfer mobile access token to web session');
        }

        localStorage.removeItem('mobile-auth-transfer');
        // We set this value in session storage for any experience to understand the app is presented inside a webview
        sessionStorage.setItem('mobile-experience', '1');
        clearPersistedParameters();
        location.replace(getSanitizedRedirectUri(decodeURIComponent(String(redirectUrl))) as string);
      } catch (error) {
        if (error instanceof Error) {
          captureException(error, 'mobile-auth-transfer');
        } else {
          captureException(
            new Error('[mobile-auth-transfer]: Unknown error, failed to transfer mobile access token to web session'),
            'mobile-auth-transfer',
          );
        }

        setError(getContentByPath('mobileAuthTransition.sessionTransferFailure'));
      }
    },
    [getContentByPath, redirectUrl],
  );

  useEffect(() => {
    if (!isProcessingAuth && typeof accessToken === 'string') {
      setIsProcessingAuth(true);
      initializeAuthProcess(accessToken);
    }
  }, [isProcessingAuth, initializeAuthProcess, accessToken]);

  if (!accessToken) {
    captureMessage('[mobile-auth-transfer]: Invalid query params, token is missing', 'mobile-auth-transfer');
    return (
      <Confirmation
        title={getContentByPath('mobileAuthTransition.invalidToken.title')}
        description={getContentByPath<string>('mobileAuthTransition.invalidToken.description')}
        icon={<Warning fill='#e02d21' />}
        testId='route-/auth/mobile'
      />
    );
  }

  if (error) {
    return (
      <Confirmation
        testId='route-//auth/mobile'
        title={getContentByPath('mobileAuthTransition.invalidToken.title')}
        description={error}
        icon={<Warning fill='#e02d21' />}
      />
    );
  }

  return <Loader />;
};
